import { SERVICE_FAMILIES } from '../../config/servicesNavigation.js';
import { resourceCategories, marketplaceHubs, resourceTools } from '../../data/resourceCatalog.js';
import { SMART_GUIDE_REGISTRY } from '../../data/smartGuideRegistry.js';

// Build a unified searchable catalog once
const buildSearchIndex = () => {
  const items = [];

  // 1. Add all services and capabilities from canonical taxonomy
  if (Array.isArray(SERVICE_FAMILIES)) {
    SERVICE_FAMILIES.forEach((family) => {
      // Add the service family overview
      items.push({
        id: `family-${family.slug}`,
        title: family.fullTitle,
        shortTitle: family.railLabel,
        description: family.shortOverview || family.description,
        path: family.overviewRoute,
        category: family.railLabel,
        categoryPath: family.overviewRoute,
        type: 'category',
        icon: family.icon,
        badgeColor: family.badgeColor || '#2563eb',
        keywords: [
          family.slug,
          family.railLabel.toLowerCase(),
          family.fullTitle.toLowerCase(),
          'service',
          'agency',
        ],
      });

      // Add each capability
      if (Array.isArray(family.capabilities)) {
        family.capabilities.forEach((cap) => {
          const kw = [
            cap.label.toLowerCase(),
            family.railLabel.toLowerCase(),
            family.slug,
            'service',
          ];

          // Domain synonyms
          if (cap.label.toLowerCase().includes('facebook') || cap.label.toLowerCase().includes('meta')) {
            kw.push('facebook', 'fb', 'meta', 'meta ads', 'instagram', 'social media');
          }
          if (cap.label.toLowerCase().includes('instagram')) {
            kw.push('instagram', 'ig', 'meta', 'reels', 'social media', 'stories');
          }
          if (cap.label.toLowerCase().includes('amazon')) {
            kw.push('amazon', 'fba', 'acos', 'ppc', 'ecommerce', 'seller');
          }
          if (cap.label.toLowerCase().includes('seo')) {
            kw.push('seo', 'google', 'rankings', 'search engine', 'traffic');
          }
          if (cap.label.toLowerCase().includes('web') || cap.label.toLowerCase().includes('app')) {
            kw.push('website', 'react', 'node', 'frontend', 'software', 'development');
          }
          if (cap.label.toLowerCase().includes('logo') || cap.label.toLowerCase().includes('brand')) {
            kw.push('logo', 'branding', 'identity', 'design', 'graphic');
          }

          items.push({
            id: `service-${cap.route}`,
            title: cap.label,
            description: cap.description,
            path: cap.route,
            category: family.railLabel,
            categoryPath: family.overviewRoute,
            type: 'service',
            icon: family.icon,
            badge: 'Service',
            badgeColor: family.badgeColor || '#10b981',
            keywords: kw,
          });
        });
      }
    });
  }

  // 2. Add all interactive tools and calculators
  if (Array.isArray(resourceTools)) {
    resourceTools.forEach((tool) => {
      const isCalculator = tool.path.includes('calculator') || tool.title.toLowerCase().includes('calculator');
      const kw = [
        tool.title.toLowerCase(),
        tool.category.toLowerCase(),
        isCalculator ? 'calculator' : 'tool',
      ];

      if (tool.slug.includes('schema') || tool.slug.includes('meta') || tool.slug.includes('robots')) {
        kw.push('seo', 'google', 'search');
      }
      if (tool.slug.includes('image') || tool.slug.includes('compress')) {
        kw.push('image', 'photo', 'compressor', 'webp', 'png');
      }

      items.push({
        id: `tool-${tool.slug}`,
        title: tool.title,
        description: tool.description,
        path: tool.path,
        category: tool.category,
        categoryPath: '/tools/',
        type: isCalculator ? 'calculator' : 'tool',
        icon: tool.icon || '🛠️',
        badge: tool.badge || (isCalculator ? 'Calculator' : 'Tool'),
        badgeColor: isCalculator ? '#f59e0b' : '#3b82f6',
        keywords: kw,
      });
    });
  }

  // 3. Add Marketplace hubs & calculators
  if (Array.isArray(marketplaceHubs)) {
    marketplaceHubs.forEach((hub) => {
      const kw = [
        hub.title.toLowerCase(),
        hub.marketplace ? hub.marketplace.toLowerCase() : '',
        'marketplace',
        'calculator',
        'ecommerce',
      ];
      if (hub.marketplace === 'Zepto' || hub.marketplace === 'Blinkit') {
        kw.push('quick commerce', 'dark store', 'delivery');
      }

      items.push({
        id: `hub-${hub.slug}`,
        title: hub.title,
        description: hub.description,
        path: hub.path,
        category: hub.category || 'Quick Commerce & Marketplaces',
        categoryPath: '/tools/marketplace/',
        type: 'calculator',
        icon: hub.icon || '🛒',
        badge: hub.marketplace || 'Marketplace',
        badgeColor: '#ec4899',
        keywords: kw,
      });
    });
  }

  // 4. Add smart guide registry items (for social media & specialized tools)
  if (Array.isArray(SMART_GUIDE_REGISTRY)) {
    SMART_GUIDE_REGISTRY.forEach((reg) => {
      // Don't duplicate if path already exists
      if (!items.some((i) => i.path === reg.path)) {
        const kw = [
          reg.title.toLowerCase(),
          ...(reg.aliases || []).map((a) => a.toLowerCase()),
          ...(reg.goals || []).map((g) => g.toLowerCase()),
          reg.industry || '',
        ];

        if (reg.title.toLowerCase().includes('facebook') || (reg.aliases && reg.aliases.includes('facebook'))) {
          kw.push('facebook', 'fb', 'meta');
        }

        items.push({
          id: `reg-${reg.id}`,
          title: reg.title,
          description: reg.description,
          path: reg.path,
          category: reg.industry === 'creator' ? 'Social Media & Creator' : 'Tools & Guides',
          categoryPath: '/tools/',
          type: reg.type === 'service' ? 'service' : reg.type === 'calculator' ? 'calculator' : 'tool',
          icon: reg.type === 'calculator' ? '🧮' : '⚡',
          badge: reg.type === 'service' ? 'Service' : 'Tool',
          badgeColor: '#6366f1',
          keywords: kw,
        });
      }
    });
  }

  // 5. Ensure Meta & Facebook entries are explicitly indexed with top relevance
  const hasFb = items.some((i) => i.title.toLowerCase().includes('facebook marketing'));
  if (!hasFb) {
    items.push({
      id: 'service-facebook-marketing',
      title: 'Facebook Marketing & Meta Ads',
      description: 'Performance-driven Facebook & Instagram Meta ad funnels, CAPI tracking, and scaling.',
      path: '/services/digital-marketing/facebook-marketing/',
      category: 'Digital Marketing & Growth',
      categoryPath: '/services/digital-marketing/',
      type: 'service',
      icon: '📈',
      badge: 'Service',
      badgeColor: '#10b981',
      keywords: ['facebook', 'facebook marketing', 'meta', 'meta ads', 'fb ads', 'retargeting', 'instagram ads', 'social media marketing'],
    });
  }

  return items;
};

export const UNIFIED_CATALOG = buildSearchIndex();

// Curated Top Categories for Quick Access
export const POPULAR_CATEGORIES = [
  {
    id: 'digital-marketing',
    title: 'Digital Marketing',
    desc: 'Facebook, Instagram, Meta Ads & SEO',
    icon: '📈',
    color: '#10b981',
    query: 'facebook marketing',
    path: '/services/digital-marketing/',
  },
  {
    id: 'ecommerce',
    title: 'E-Commerce & Marketplaces',
    desc: 'Amazon, Flipkart, Zepto & Blinkit',
    icon: '🛒',
    color: '#ec4899',
    query: 'amazon calculator',
    path: '/tools/marketplace/',
  },
  {
    id: 'web-dev',
    title: 'Web & App Development',
    desc: 'React, Node.js, SaaS & Custom Apps',
    icon: '💻',
    color: '#3b82f6',
    query: 'website development',
    path: '/services/software-engineering/',
  },
  {
    id: 'branding',
    title: 'UI/UX & Brand Design',
    desc: 'Logo Marks, Visual Systems & Packaging',
    icon: '🎨',
    color: '#e11d48',
    query: 'logo brand design',
    path: '/services/brand-creative/',
  },
  {
    id: 'seo-tools',
    title: 'SEO & Search Tools',
    desc: 'Schema Generator & Meta Tags',
    icon: '🔍',
    color: '#0f766e',
    query: 'seo schema meta',
    path: '/tools/seo/schema-generator/',
  },
  {
    id: 'quick-commerce',
    title: 'Quick Commerce Planners',
    desc: 'Blinkit, Zepto, Swiggy Instamart margins',
    icon: '⚡',
    color: '#f59e0b',
    query: 'zepto blinkit',
    path: '/tools/quick-commerce/zepto-fee-calculator/',
  },
];

// Problem-Solver Quick Prompts
export const GOAL_PROMPTS = [
  { label: 'Puzzled what content to make? Steal viral teardowns', query: 'instagram' },
  { label: 'Create Viral Instagram Reels Script (5K+ hooks)', query: 'reel script' },
  { label: 'Design Logo, Brand Identity & Matching Palettes', query: 'branding' },
  { label: 'Reduce Amazon ACoS, FBA & Marketplace Fees', query: 'amazon' },
  { label: 'Scale Facebook & Instagram Meta Ads', query: 'facebook' },
  { label: 'Speed up website: Compress & optimize images', query: 'compressor' },
  { label: 'Build Custom Website / High-Converting Web App', query: 'web application' },
  { label: 'Calculate Quick Commerce Margin (Zepto, Blinkit)', query: 'zepto blinkit' },
  { label: 'Generate SEO Schema & Rich Snippet Meta Tags', query: 'seo meta' },
];

/**
 * Searches the unified catalog with real-time tokenized relevance
 */
export const searchNavigator = (rawQuery) => {
  const query = (rawQuery || '').trim().toLowerCase();
  if (!query) {
    return {
      hasResults: false,
      categories: [],
      services: [],
      tools: [],
      allMatches: [],
    };
  }

  const queryTokens = query.split(/\s+/).filter(Boolean);

  const scored = UNIFIED_CATALOG.map((item) => {
    let score = 0;
    const titleLower = item.title.toLowerCase();
    const descLower = (item.description || '').toLowerCase();
    const catLower = (item.category || '').toLowerCase();
    const kwText = (item.keywords || []).join(' ');

    // Exact title match gets highest score
    if (titleLower === query) score += 150;
    else if (titleLower.startsWith(query)) score += 100;
    else if (titleLower.includes(query)) score += 75;

    // Direct keyword match
    if (kwText.includes(query)) score += 60;

    // Check token matches
    let tokenMatches = 0;
    for (const token of queryTokens) {
      if (token.length >= 2) {
        if (titleLower.includes(token)) {
          score += 40;
          tokenMatches++;
        } else if (kwText.includes(token)) {
          score += 30;
          tokenMatches++;
        } else if (descLower.includes(token)) {
          score += 15;
          tokenMatches++;
        } else if (catLower.includes(token)) {
          score += 20;
          tokenMatches++;
        }
      }
    }

    // Boost if multiple tokens matched
    if (tokenMatches > 1) {
      score += tokenMatches * 15;
    }

    return { item, score };
  })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  const topItems = scored.slice(0, 12).map((entry) => entry.item);

  // Group items by type
  const services = topItems.filter((i) => i.type === 'service');
  const tools = topItems.filter((i) => i.type === 'tool' || i.type === 'calculator');
  const categories = topItems.filter((i) => i.type === 'category');

  // Also collect unique category names from matched items
  const matchingCategoryNames = Array.from(
    new Set(topItems.map((i) => i.category).filter(Boolean))
  ).slice(0, 5);

  return {
    hasResults: topItems.length > 0,
    services,
    tools,
    categories,
    matchingCategoryNames,
    allMatches: topItems,
    topMatch: topItems[0] || null,
  };
};
